import type { ReactNode } from 'react'

import estilos from './OrdenTabla.module.css'

/**
 * Por qué columna y en qué sentido está ordenada una tabla.
 *
 * Vive fuera del componente porque el orden viaja hasta la consulta: con
 * miles de filas paginadas, ordenar en el navegador solo ordenaría la página
 * que se está viendo, que es justo lo que la persona no quiere.
 */
export type Orden<Campo extends string> = {
  campo: Campo
  ascendente: boolean
}

type PropsEncabezado<Campo extends string> = {
  campo: Campo
  orden: Orden<Campo>
  onCambiar: (orden: Orden<Campo>) => void
  children: ReactNode
  /** Las columnas de cifras van alineadas a la derecha. */
  derecha?: boolean
  ancho?: number
  /**
   * Sentido de la primera vez que se toca. Los nombres arrancan de la A a la
   * Z; las cantidades y las fechas, de mayor a menor, porque lo que se busca
   * es lo más grande o lo más reciente.
   */
  primeroDescendente?: boolean
}

/** Encabezado que ordena la tabla al tocarlo. */
export function ThOrdenable<Campo extends string>({
  campo,
  orden,
  onCambiar,
  children,
  derecha = false,
  ancho,
  primeroDescendente = false,
}: PropsEncabezado<Campo>) {
  const activo = orden.campo === campo

  function alternar() {
    onCambiar({
      campo,
      // Tocar la columna activa invierte el sentido; tocar otra la ordena
      // como corresponda a lo que contiene.
      ascendente: activo ? !orden.ascendente : !primeroDescendente,
    })
  }

  return (
    <th
      style={ancho ? { width: ancho } : undefined}
      aria-sort={activo ? (orden.ascendente ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`${estilos.boton} ${derecha ? estilos.derecha : ''} ${
          activo ? estilos.activo : ''
        }`}
        onClick={alternar}
      >
        {children}
        <span className={estilos.flecha} aria-hidden="true">
          {activo ? (orden.ascendente ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  )
}
